import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  DeviceInfoModel,
  DeviceMessageModel,
  RealtimeDeviceStatus,
  convertToRealtimeStatus,
} from 'app/modules/gestao-operacao/fleet-monitoring/device/device.model';
import { NavigationTabModel } from 'app/modules/gestao-operacao/fleet-monitoring/navigation-tab/navigation-tab.model';
import {
  buildMachineTelemetryView,
  extractMachineUpdate,
} from 'app/modules/gestao-operacao/fleet-monitoring/machine-tab/machine-telemetry.decoder';
import {
  MachineTelemetryView,
  MachineUpdateSnapshot,
} from 'app/modules/gestao-operacao/fleet-monitoring/machine-tab/machine-telemetry.model';
import { DeviceService } from '../../../../core/services/device.service';
import {
  DynamicMessageOperationTabModel,
  StaticMessageOperationTabModel,
} from '../operation-tab/operation-tab.model';
import { AgresSpinnerService } from 'app/shared/components/agres-spinner/agres-spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { OpType } from '../enums/operation-type.enum';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

interface DeviceProperties {
  [key: string]: [number, string];
}

interface Device {
  properties?: DeviceProperties;
}

interface WidthResult {
  totalWidth: number;
  unit: string;
}

/** Item of the `update` sorted set: base64 message scored by `device_update.timestamp`. */
interface AgronaveUpdateMessage {
  value: string;
  score: number;
}

@Component({
  standalone: false,
  selector: 'app-realtime-device-monitoring',
  templateUrl: './realtime-device-monitoring.component.html',
  styleUrls: ['./realtime-device-monitoring.component.scss'],
})
export class RealtimeDeviceMonitoringComponent implements OnInit, OnDestroy {
  isIsobus: boolean = false;
  isTaskActive: boolean = false;
  deviceInfo: DeviceInfoModel;
  deviceInfoArray: DeviceInfoModel[] = [];
  circles = [];
  oldMessages = [];
  currentTab = 'operation';
  totalTabInfo: any;
  navigationTabInfo: NavigationTabModel;
  operationTabInfo: DynamicMessageOperationTabModel;
  staticMessageOperationTabInfo: StaticMessageOperationTabModel;
  machineTabInfo: MachineTelemetryView;
  private machineUpdateSnapshot: MachineUpdateSnapshot;
  /** Newest `update` already inspected for a `machine_update` section. */
  private machineScanTimestamp = Number.NEGATIVE_INFINITY;
  private isoviewRealtimeMsgsInterval: NodeJS.Timeout;
  private agronaveDeviceUpdateInterval: NodeJS.Timeout;
  private agronaveTaskUpdateInterval: NodeJS.Timeout;

  private enableProcessing = true;
  private getLastMessageOnly = false;
  private firstLoad = true;
  private lastmessage: any;
  private timestamps = [];

  private ecuDDIMap = new Map([
    ['lifetimeTotalArea', 271],
    ['lifetimeEffectiveTotalDistance', 272],
    ['lifetimeEffectiveTotalTime', 274],
    ['lifetimeApplicationTotalVolume', 325],
    ['lifetimeApplicationTotalMass', 266],
    ['lifetimeApplicationTotalCount', 267],
  ]);

  private taskDDIMap = new Map([
    ['effectiveTotalDistance', 117],
    ['totalArea', 116],
    ['effectiveTotalTime', 119],
    ['applicationTotalVolumeAsL', 80],
    ['applicationTotalMassInKg', 81],
    ['applicationTotalCount', 82],
  ]);

  private ratesDDIMap = new Map([
    ['actualVolumePerAreaApplicationRateAsMm3DivM2', 2],
    ['actualCountPerAreaApplicationRate', 12],
    ['actualMassPerAreaApplicationRate', 7],
  ]);

  private barSizeDDIMap = new Map([['maximumWorkingWidth', 70]]);

  private DDIMap: { [x: string]: any };
  devices = [];
  private selectedDevice: string;
  private device: any;
  private currentRate: Number = 0;
  private currentRateUnit: string = '';
  private sectState: Number[] = [0];
  private implementWidth: Number = 10;
  private taskInfoMsgAvailable: boolean = false;
  private appliedArea: Number = 0;
  private isIsobusDeviceChanged = false;
  isCurrentDeviceConnected = false;

  constructor(
    private router: Router,
    private deviceService: DeviceService,
    private route: ActivatedRoute,
    public _location: Location,
    private readonly loadingAgresSpinner: AgresSpinnerService,
    private readonly translateService: TranslateService,
  ) {
    this.DDIMap = {
      ...Object.fromEntries(this.createDDIMap(this.ecuDDIMap)),
      ...Object.fromEntries(this.createDDIMap(this.taskDDIMap)),
      ...Object.fromEntries(this.createDDIMap(this.ratesDDIMap)),
      ...Object.fromEntries(this.createDDIMap(this.barSizeDDIMap)),
    };
  }

  ngOnInit(): void {
    this.translateService.get('global.loading.map-wait').subscribe((res) => {
      this.loadingAgresSpinner.toShow(res);
    });
    this.route.params.subscribe((params) => {
      if (params.type == 'agronave') {
        this.fetchAgronaveTaskInfoMsgs(params.id);
      } else {
        this.fetchIsoviewRealtimeMsgs(params.id);
      }
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.isoviewRealtimeMsgsInterval);
    clearTimeout(this.agronaveDeviceUpdateInterval);
    clearTimeout(this.agronaveTaskUpdateInterval);
  }

  refresh(): void {
    window.location.reload();
  }

  onDeviceSelect() {
    this.device = this.devices.find(
      (device) => device.deviceDesignator === this.selectedDevice,
    );
    this.getLastMessageOnly = false;
    this.isIsobusDeviceChanged = true;
  }

  createDDIMap(map) {
    return new Map(
      [...map].map(([key, value]) => [
        value.toString(16).padStart(4, '0').toUpperCase(),
        key,
      ]),
    );
  }

  toHexSigned(value) {
    let hex = (value & 0xffff).toString(16).toUpperCase();
    return hex.padStart(4, '0');
  }

  fetchAgronaveUpdateMsgs(deviceId: string) {
    this.agronaveDeviceUpdateInterval = setInterval(() => {
      this.deviceService
        .getUpdateMessages(deviceId)
        .pipe(
          catchError((error) => {
            console.error(
              `fetchAgronaveUpdateMsgs: Error ${error.status}.`,
              error,
            );
            return of(undefined);
          }),
        )
        .subscribe((deviceMessage) => {
          if (deviceMessage !== undefined) {
            this.parseAgronaveRealtimeMsg(deviceMessage.messages);
          }
        });
    }, 3000);
  }

  fetchAgronaveTaskInfoMsgs(deviceId: string) {
    this.agronaveTaskUpdateInterval = setInterval(() => {
      this.deviceService
        .getTaskInfoMessages(deviceId)
        .pipe(
          catchError((error) => {
            console.error(
              `fetchAgronaveTaskInfoMsgs: Error ${error.status}.`,
              error,
            );
            return of(undefined);
          }),
        )
        .subscribe((deviceMessage) => {
          if (deviceMessage !== undefined) {
            if (
              deviceMessage.messages.length > 0 &&
              this.taskInfoMsgAvailable === false
            ) {
              this.parseAgronaveStaticMsg(deviceId, deviceMessage.messages);
            } else {
              this.taskInfoMsgAvailable = false;
              if (this.firstLoad) {
                this.fetchAgronaveUpdateMsgs(deviceId);
                this.firstLoad = false;
              }
            }
          }
        });
    }, 3000);
  }

  parseAgronaveStaticMsg(deviceId, deviceMessages) {
    this.taskInfoMsgAvailable = true;
    const taskInfoMessage = this.decodeBase64ToJson(deviceMessages[0]);
    this.staticMessageOperationTabInfo = new StaticMessageOperationTabModel(
      '',
      taskInfoMessage.field_name,
      taskInfoMessage.task_name,
      '0',
      [],
      0,
      taskInfoMessage.farm_name,
      taskInfoMessage.machine,
      taskInfoMessage.sector,
      taskInfoMessage.operator,
    );

    if (taskInfoMessage.operation === 'Navigation') {
      if (this.firstLoad) {
        this.fetchAgronaveUpdateMsgs(deviceId);
        this.firstLoad = false;
      }
    } else {
      this.deviceService.getTaskDDOPMessages(deviceId).subscribe(
        (ddop) => {
          if (ddop?.messages?.[0]) {
            this.deviceService.ddopParsed(ddop.messages[0]).subscribe(
              (response: Array<any>) => {
                const devices = Array.isArray(response) ? response : [];

                const isDifferent =
                  devices.length !== this.devices.length ||
                  devices.some(
                    (d, i) =>
                      d.deviceDesignator !== this.devices[i]?.deviceDesignator,
                  );

                if (devices.length > 0 && isDifferent) {
                  this.devices = devices;
                  this.selectedDevice = devices[0]?.deviceDesignator;
                  this.device = devices[0];
                }
                if (this.firstLoad) {
                  this.fetchAgronaveUpdateMsgs(deviceId);
                  this.firstLoad = false;
                }
              },
              (error) => {
                this.taskInfoMsgAvailable = false;
                console.error('Error during DDOP parser POST:', error);
              },
            );
          } else {
            this.taskInfoMsgAvailable = false;
          }
        },
        (err: any) => {
          this.taskInfoMsgAvailable = false;
          console.error('Error fetching DDOP messages:', err);
        },
      );
    }
  }

  applyScaleAndDecimals(
    value: number,
    scale: number,
    numberOfDecimals: number,
  ) {
    const scaledValue = value * scale;
    return scaledValue.toFixed(numberOfDecimals);
  }

  parseStateValues(sections): number[] {
    const sectionStates: number[] = [];
    for (const section of sections) {
      for (let i = 0; i < 32; i += 2) {
        let state = (Number(section.value) >>> i) & 0x03;
        if (state > 1) {
          state = 0;
        }

        sectionStates.push(state);
      }
    }
    return sectionStates;
  }

  parseAgronaveRealtimeMsg(deviceMessages: any) {
    if (
      !this.enableProcessing ||
      !Array.isArray(deviceMessages) ||
      deviceMessages.length === 0
    ) {
      return;
    }

    this.updateMachineTelemetry(deviceMessages);

    if (this.getLastMessageOnly && this.lastmessage) {
      this.processSingleMessage(deviceMessages[deviceMessages.length - 1]);
    } else {
      this.oldMessages = [];
      this.taskInfoMsgAvailable = true;
      deviceMessages.forEach((message) => this.processMessage(message));
      this.taskInfoMsgAvailable = false;
      this.updateLastMessage(
        this.decodeBase64ToJson(deviceMessages[deviceMessages.length - 1].value)
          .device_update,
      );
      this.circles = this.oldMessages;
      this.enableProcessing = false;
    }
  }

  /**
   * Keeps the newest `machine_update` section and rebuilds the machine tab.
   *
   * @remarks The section only comes in some `update` messages (machine telemetry
   * license, nominally every 10 s), so a message without it keeps the previous
   * snapshot. Only messages newer than the last scan are decoded, since the
   * endpoint returns the whole 30-minute window on every poll.
   */
  private updateMachineTelemetry(deviceMessages: AgronaveUpdateMessage[]) {
    const latestDeviceTimestamp =
      deviceMessages[deviceMessages.length - 1].score;
    let snapshot: MachineUpdateSnapshot | undefined;
    let index = deviceMessages.length - 1;
    while (
      snapshot === undefined &&
      index >= 0 &&
      deviceMessages[index].score > this.machineScanTimestamp
    ) {
      snapshot = extractMachineUpdate(
        this.decodeBase64ToJson(deviceMessages[index].value),
      );
      index--;
    }
    this.machineScanTimestamp = Math.max(
      this.machineScanTimestamp,
      latestDeviceTimestamp,
    );

    if (snapshot !== undefined) {
      this.machineUpdateSnapshot = snapshot;
    }
    if (this.machineUpdateSnapshot !== undefined) {
      this.machineTabInfo = buildMachineTelemetryView(
        this.machineUpdateSnapshot,
        latestDeviceTimestamp,
      );
    }
  }

  private processSingleMessage(deviceMessage: any) {
    this.loadingAgresSpinner.toHide();
    const decodedMessage = this.decodeBase64ToJson(deviceMessage.value);
    const deviceUpdateMessage = decodedMessage.device_update;
    const taskUpdateMessage = decodedMessage.task_update;
    if (deviceUpdateMessage.timestamp >= this.lastmessage.timestamp) {
      this.isIsobus = deviceUpdateMessage.is_isobus;
      this.isTaskActive = deviceUpdateMessage.task_active;
      if (
        this.lastmessage &&
        (this.lastmessage.task_active !== deviceUpdateMessage.task_active ||
          this.lastmessage.is_isobus !== deviceUpdateMessage.is_isobus)
      ) {
        this.refresh();
      }
      this.updateDeviceData(deviceUpdateMessage, taskUpdateMessage);
      this.deviceInfo = this.createDeviceInfo(deviceUpdateMessage);
      if (this.isIsobusDeviceChanged) {
        this.isIsobusDeviceChanged = false;
      }
      this.updateLastMessage(deviceUpdateMessage);
    }
  }

  private processMessage(deviceMessage: any) {
    const decodedMessage = this.decodeBase64ToJson(deviceMessage.value);
    const deviceUpdateMessage = decodedMessage.device_update;
    this.isIsobus = deviceUpdateMessage.is_isobus;
    this.isTaskActive = deviceUpdateMessage.task_active;
    const taskUpdateMessage = decodedMessage?.task_update || {};
    this.updateDeviceData(deviceUpdateMessage, taskUpdateMessage);
    this.saveHistoricalData(deviceUpdateMessage);
  }

  private updateDeviceData(deviceUpdateMessage: any, taskUpdateMessage: any) {
    if (!this.isIsobus) {
      this.handleNonIsobusDevice(deviceUpdateMessage, taskUpdateMessage);
    } else if (this.isTaskActive && this.taskInfoMsgAvailable) {
      this.handleActiveTask(deviceUpdateMessage, taskUpdateMessage);
    } else {
      this.handleInactiveTask(deviceUpdateMessage);
    }
  }

  private handleNonIsobusDevice(
    deviceUpdateMessage: any,
    taskUpdateMessage: any,
  ) {
    if (this.isTaskActive) {
      this.implementWidth = taskUpdateMessage.boom_length ?? 0;
      this.currentRate = 0;
      this.sectState = [taskUpdateMessage.section_state ? 1 : 0];
      this.appliedArea = taskUpdateMessage.applied_area ?? 0;

      this.updateOperationInfo(deviceUpdateMessage);
    }
    this.updateNavigationInfo(deviceUpdateMessage);
  }

  private handleActiveTask(deviceUpdateMessage: any, taskUpdateMessage: any) {
    const ddiValues = this.extractDDIValues(taskUpdateMessage);
    const dpdValues = this.mapDDIToDPD(ddiValues);

    if (ddiValues.length === 0) {
      this.isCurrentDeviceConnected = false;
    } else {
      this.isCurrentDeviceConnected = true;
    }

    this.updateImplementWidth(dpdValues);
    this.updateCurrentRate(dpdValues);
    this.updateSectState(dpdValues);

    const calculatedTotals = this.calculateTotals(taskUpdateMessage);
    this.totalTabInfo =
      calculatedTotals.length > 0 ? calculatedTotals : this.totalTabInfo;

    this.updateOperationInfo(deviceUpdateMessage);
    this.updateNavigationInfo(deviceUpdateMessage);
  }

  private handleInactiveTask(deviceUpdateMessage: any) {
    this.updateNavigationInfo(deviceUpdateMessage);
  }

  private extractDDIValues(ddi: any): any[] {
    const elementNumbers = new Set(this.device?.devicesElementNumbers || []);

    if (ddi && Object.keys(ddi).length !== 0 && elementNumbers.size !== 0) {
      return Object.entries(ddi)
        .map(([key, value]) => {
          const [hexPart, elementNumber] = key.split('_');
          const label = this.determineLabel(hexPart);

          if (!label || !elementNumbers.has(elementNumber)) return null;

          const type = this.determineType(label);
          return { label, type, dpd: key, value };
        })
        .filter(Boolean);
    }
    return [];
  }

  private determineLabel(hexPart: string): string | null {
    return (
      this.DDIMap[hexPart] ||
      (parseInt(hexPart, 16) >= parseInt('A1', 16) &&
      parseInt(hexPart, 16) <= parseInt('B0', 16)
        ? 'sectState'
        : null)
    );
  }

  private determineType(label: string): string | null {
    if (this.ratesDDIMap.has(label)) return 'rate';
    if (this.ecuDDIMap.has(label)) return 'lifetime';
    if (this.taskDDIMap.has(label)) return 'task';
    if (this.barSizeDDIMap.has(label)) return 'barSize';
    if (label === 'sectState') return 'sectState';
    return null;
  }

  private mapDDIToDPD(ddiValues: any[]): any[] {
    return ddiValues
      .map((item) => {
        if (item.label === 'sectState') {
          return {
            label: item.label,
            type: item.type,
            value: item.value,
            unit: '',
          };
        }

        const dpdData = this.device.dpds[item.dpd];
        if (dpdData) {
          const finalValue = this.applyScaleAndDecimals(
            item.value as number,
            dpdData.scale,
            dpdData.numberOfDecimals,
          );
          return {
            label: dpdData.label,
            type: item.type,
            value: finalValue,
            unit: dpdData.unit,
          };
        }
        return null;
      })
      .filter(Boolean);
  }

  private updateCurrentRate(dpdValues: any[]) {
    const filteredRate = dpdValues.find((item) => item.type === 'rate');
    if (filteredRate) {
      const value = Number(filteredRate.value);
      this.currentRate = value * 0.01;

      if (filteredRate.unit.includes('mm³/m²')) {
        this.currentRateUnit = 'L/ha';
      } else if (filteredRate.unit.includes('mg/m²')) {
        this.currentRateUnit = 'Kg/ha';
      } else {
        this.currentRate = value;
        this.currentRateUnit = filteredRate.unit;
      }
    }
  }

  private updateSectState(dpdValues: any[]) {
    const filteredSectState = dpdValues.filter(
      (item) => item.type === 'sectState',
    );
    if (filteredSectState.length > 0) {
      this.sectState = this.parseStateValues(filteredSectState).slice(
        0,
        this.device.references.Section.length,
      );
    }
  }

  private calculateWorkingWidth(device: Device): WidthResult {
    if (!device?.properties) return { totalWidth: 0, unit: 'mm' };

    let totalWidth = 0;
    let unit = 'mm';

    for (const key in device.properties) {
      if (key.includes('maximumWorkingWidth')) {
        totalWidth = device.properties[key][0];
        unit = device.properties[key][1];
        break;
      }
    }

    if (totalWidth === 0) {
      for (const key in device.properties) {
        if (key.includes('actualWorkingWidth')) {
          totalWidth += device.properties[key][0];
          unit = device.properties[key][1];
        }
      }
    }

    return { totalWidth, unit };
  }

  private convertToMeters(value: number, unit: string): number {
    const conversionRates: Record<string, number> = {
      mm: 1 / 1000,
      cm: 1 / 100,
      m: 1,
    };

    const normalizedUnit = Object.keys(conversionRates).find((u) =>
      unit.toLowerCase().includes(u),
    );

    return value * (normalizedUnit ? conversionRates[normalizedUnit] : 1);
  }

  private updateImplementWidth(dpdValues: any[]) {
    const barWidth = this.calculateWorkingWidth(this.device);
    if (barWidth.totalWidth > 0) {
      this.implementWidth = this.convertToMeters(
        barWidth.totalWidth,
        barWidth.unit,
      );
    } else {
      const barSizes = dpdValues.filter((item) => item.type === 'barSize');
      if (barSizes.length > 0) {
        const maxBarSize = barSizes.reduce((max, current) => {
          return parseInt(current.value) > parseInt(max.value) ? current : max;
        });
        this.implementWidth = this.convertToMeters(
          parseInt(maxBarSize.value),
          maxBarSize.unit,
        );
      }
    }
  }

  private calculateTotals(taskUpdateMessage: any): any[] {
    if (
      this.device?.totals &&
      taskUpdateMessage &&
      Object.keys(taskUpdateMessage).length !== 0
    ) {
      return Object.keys(this.device.totals).reduce((result, key) => {
        if (taskUpdateMessage[key] !== undefined) {
          const { label, scale, numberOfDecimals, unit } =
            this.device.totals[key];
          const value = this.applyScaleAndDecimals(
            taskUpdateMessage[key],
            scale,
            numberOfDecimals,
          );
          result.push({ label, unit, value });
        }
        return result;
      }, []);
    }
    return [];
  }

  private updateOperationInfo(deviceUpdateMessage: any) {
    this.operationTabInfo = new DynamicMessageOperationTabModel(
      this.implementWidth as number,
      Array.isArray(this.device?.references?.Section)
        ? this.device.references.Section.length
        : 0,
      '0',
      '0',
      (deviceUpdateMessage.total_distance / 1000).toFixed(1),
      this.currentRate as number,
      this.currentRateUnit ?? '',
      this.sectState?.map((num) => String(num)) ?? [],
      0,
      this.isIsobus ? OpType.ISOBUS : OpType.NAVIGATION,
      deviceUpdateMessage.speed,
      this.appliedArea as number,
    );
  }

  private updateNavigationInfo(deviceUpdateMessage: any) {
    this.navigationTabInfo = new NavigationTabModel(
      deviceUpdateMessage.latitude,
      deviceUpdateMessage.longitude,
      deviceUpdateMessage.altitude,
      deviceUpdateMessage.satellites,
      deviceUpdateMessage.position_type,
      deviceUpdateMessage.gps_source,
      deviceUpdateMessage.isSteeringEngaged,
      'agronave',
      deviceUpdateMessage.equipment_name,
      deviceUpdateMessage.serial_number,
      deviceUpdateMessage.software_version,
    );
  }

  private createDeviceInfo(deviceUpdateMessage: any): DeviceInfoModel {
    return new DeviceInfoModel(
      deviceUpdateMessage.serial_number,
      'agronave',
      deviceUpdateMessage.task_active === true
        ? RealtimeDeviceStatus.WORKING
        : RealtimeDeviceStatus.MOVING,
      this.sectState.map((num) => String(num)),
      this.isIsobus && this.isTaskActive ? OpType.ISOBUS : OpType.NAVIGATION,
      this.implementWidth as number,
      this.currentRate as number,
      this.currentRateUnit,
      deviceUpdateMessage.latitude,
      deviceUpdateMessage.longitude,
      deviceUpdateMessage.timestamp,
      this.sectState.join(''),
      deviceUpdateMessage.speed,
      this.isIsobusDeviceChanged,
    );
  }

  private saveHistoricalData(deviceUpdateMessage: any) {
    this.oldMessages.push({
      center: {
        lat: deviceUpdateMessage.latitude,
        lng: deviceUpdateMessage.longitude,
      },
      radius: (this.implementWidth as number) / 2,
      instRate: this.currentRate,
      instRateUnit: this.currentRateUnit,
      sectionArr: this.sectState.map((num) => String(num)),
      timestamp: deviceUpdateMessage.timestamp,
      selected: false,
      gwAddr: 'agronave',
      velocity: deviceUpdateMessage.speed,
      opType: this.isIsobus ? OpType.ISOBUS : OpType.NAVIGATION,
    });

    this.timestamps.push(deviceUpdateMessage.timestamp);
  }

  private updateLastMessage(deviceUpdateMessage: any) {
    this.lastmessage = deviceUpdateMessage;
    this.timestamps.push(deviceUpdateMessage.timestamp);
  }

  decodeBase64ToJson(base64String: string): any {
    try {
      const jsonString = atob(base64String);
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Erro ao decodificar a string Base64:', error);
      return null;
    }
  }

  fetchIsoviewRealtimeMsgs(deviceId: string): void {
    this.isoviewRealtimeMsgsInterval = setInterval(() => {
      this.deviceService.getDeviceRealtime(deviceId).subscribe(
        (deviceMessages: DeviceMessageModel) => {
          this.parseIsoviewRealtimeMsgs(deviceMessages);
        },
        (err: any) => {
          console.error(err);
        },
      );
    }, 3000);
  }

  parseIsoviewRealtimeMsgs(deviceMessages): Promise<void> {
    if (this.enableProcessing) {
      if (deviceMessages.length !== 0 && Array.isArray(deviceMessages)) {
        if (this.getLastMessageOnly) {
          this.loadingAgresSpinner.toHide();
          const deviceMessage = deviceMessages[deviceMessages.length - 1];
          const dinamycMessages = deviceMessages.filter((item) => 'd' in item);
          const messagesToGet = dinamycMessages.filter((item) => {
            if (!this.timestamps.includes(item.d.timestamp)) {
              return item;
            }
          });
          if (messagesToGet.length > 0) {
            if (
              this.lastmessage.d.operation.work_status !==
              messagesToGet[0].d.operation.work_status
            ) {
              window.location.reload();
            }
            if (this.lastmessage.d.timestamp !== messagesToGet[0].d.timestamp) {
              const device: DeviceMessageModel = messagesToGet[0];
              const operationType = device.d.operation.op_type;

              const activeTime: number = device.d.operation.tot_op_e;
              const totalTime: number = device.d.operation.tot_op_t;
              const operationActiveTime = this.secondsToTime(activeTime);
              const operationTotalTime = this.secondsToTime(totalTime);
              const percentTime =
                totalTime === 0 ? 0 : (activeTime / totalTime) * 100;
              const operationTimePercent = Number(percentTime.toFixed(0));
              const numberKm = device.d.operation.travel_dist;
              const travelDistance = numberKm.toFixed(1);
              const sectionStatus = device.d.operation.sec_state;
              const numberOfSections = device.d.operation.sec_number;
              let sectionArr = [];

              if (Number(sectionStatus) === 0) {
                for (let i = 0; i < numberOfSections; i++) {
                  sectionArr.push('0');
                }
              } else {
                sectionArr = sectionStatus.split('');
              }

              this.operationTabInfo = new DynamicMessageOperationTabModel();
              this.operationTabInfo.implementWidth =
                device.d.operation.impl_width;
              this.operationTabInfo.numberOfSections =
                device.d.operation.sec_number;
              this.operationTabInfo.operationActiveTime = operationActiveTime;
              this.operationTabInfo.operationTotalTime = operationTotalTime;
              this.operationTabInfo.travelDistance = travelDistance;
              this.operationTabInfo.instRate = device.d.operation.inst_rate;
              this.operationTabInfo.sectionArr = sectionArr;
              this.operationTabInfo.operationTimePercent = operationTimePercent;
              this.operationTabInfo.operationType = operationType;
              this.operationTabInfo.speed = device.d.navigation.speed;

              this.navigationTabInfo = new NavigationTabModel(
                device.d.navigation.location.latitude,
                device.d.navigation.location.longitude,
                device.d.navigation.location.elevation,
                device.d.navigation.used_sat,
                device.d.navigation.pos_type,
              );

              const status = convertToRealtimeStatus(
                device.d.operation.work_status,
              );

              this.deviceInfo = new DeviceInfoModel(
                device.d.node_addr,
                device.d.gw_addr,
                status,
                sectionArr,
                device.d.operation.op_type,
                device.d.operation.impl_width,
                device.d.operation.inst_rate,
                '',
                device.d.navigation.location.latitude,
                device.d.navigation.location.longitude,
                device.d.timestamp,
                device.d.operation.sec_state,
                device.d.navigation.speed,
              );
              this.lastmessage = device;
              this.timestamps.push(device.d.timestamp);
            }
          }

          if ('e' in deviceMessage) {
            const device: DeviceMessageModel = deviceMessage;
            this.staticMessageOperationTabInfo =
              new StaticMessageOperationTabModel(
                device.e.crop_type,
                device.e.field_name,
                device.e.event_name,
                device.e.nozz_number,
                device.e.nozz_per_section,
                device.e.nozz_spacing,
                '',
                device.e.machine_name,
                '',
                device.e.operator,
              );
          }
          return;
        }
        for (const deviceMessage of deviceMessages) {
          if ('d' in deviceMessage) {
            this.lastmessage = deviceMessage;
            const device: DeviceMessageModel = deviceMessage;
            const operationType = device.d.operation.op_type;

            const activeTime: number = device.d.operation.tot_op_e;
            const totalTime: number = device.d.operation.tot_op_t;
            const operationActiveTime = this.secondsToTime(activeTime);
            const operationTotalTime = this.secondsToTime(totalTime);
            const percentTime =
              totalTime === 0 ? 0 : (activeTime / totalTime) * 100;
            const operationTimePercent = Number(percentTime.toFixed(0));
            const numberKm = device.d.operation.travel_dist;
            const travelDistance = numberKm.toFixed(1);
            const sectionStatus = device.d.operation.sec_state;
            const numberOfSections = device.d.operation.sec_number;
            let sectionArr = [];

            if (Number(sectionStatus) === 0) {
              for (let i = 0; i < numberOfSections; i++) {
                sectionArr.push('0');
              }
            } else {
              sectionArr = sectionStatus.split('');
            }

            this.operationTabInfo = new DynamicMessageOperationTabModel();
            this.operationTabInfo.implementWidth =
              device.d.operation.impl_width;
            this.operationTabInfo.numberOfSections =
              device.d.operation.sec_number;
            this.operationTabInfo.operationActiveTime = operationActiveTime;
            this.operationTabInfo.operationTotalTime = operationTotalTime;
            this.operationTabInfo.travelDistance = travelDistance;
            this.operationTabInfo.instRate = device.d.operation.inst_rate;
            this.operationTabInfo.sectionArr = sectionArr;
            this.operationTabInfo.operationTimePercent = operationTimePercent;
            this.operationTabInfo.operationType = operationType;

            this.navigationTabInfo = new NavigationTabModel(
              device.d.navigation.location.latitude,
              device.d.navigation.location.longitude,
              device.d.navigation.location.elevation,
              device.d.navigation.used_sat,
              device.d.navigation.pos_type,
            );

            this.oldMessages.push({
              center: {
                lat: device.d.navigation.location.latitude,
                lng: device.d.navigation.location.longitude,
              },
              radius: device.d.operation.impl_width / 2,
              instRate: device.d.operation.inst_rate,
              sectionArr: sectionArr,
              timestamp: device.d.timestamp,
              selected: false,
              gwAddr: device.d.gw_addr,
              velocity: device.d.navigation.speed,
            });
            this.timestamps.push(device.d.timestamp);
          } else if ('e' in deviceMessage) {
            const device: DeviceMessageModel = deviceMessage;
            this.staticMessageOperationTabInfo =
              new StaticMessageOperationTabModel(
                device.e.crop_type,
                device.e.field_name,
                device.e.event_name,
                device.e.nozz_number,
                device.e.nozz_per_section,
                device.e.nozz_spacing,
                '',
                device.e.machine_name,
                '',
                device.e.operator,
              );
          }
        }
        this.circles = this.oldMessages;
        this.enableProcessing = false;
      }
    }
  }

  onProcessingComplete() {
    if (this.deviceInfoArray.length > 0) {
      this.deviceInfo = this.deviceInfoArray.shift();
    } else {
      this.deviceInfoArray = [];
      this.enableProcessing = true;
      this.getLastMessageOnly = true;
    }
  }

  secondsToTime(seconds: number): string {
    const totalSeconds: number = seconds;
    const hours: number = Math.floor(totalSeconds / 3600);
    const minutes: number = Math.floor((totalSeconds - hours * 3600) / 60);
    let result: string = String(hours < 10 ? '0' + hours : hours);
    result += ':' + (minutes < 10 ? '0' + minutes : minutes);
    return result;
  }

  dashboard() {
    this.router.navigate(['/']);
  }

  goBack() {
    this._location.back();
  }
}
